// Libraries
import { memo } from 'react';
import { flexRender } from '@tanstack/react-table';
import { Td } from '@chakra-ui/react';
// Hooks
import { useBorderRight } from '@hooks/index';
// Types
import { TableCellProps } from './TableCell.types';

/**
 * TableCell component represents a cell within a table.
 *
 * @component
 * @template TableBody - The type representing the table body data.
 * @template ColumnTableNames - The type representing the column table names.
 * @param {TableCellProps<TableBody, ColumnTableNames>} props - The props for the TableCell component.
 * @param {BorderRightBreakpoint<CellsName>} props.cellsWithBorderRightMap - Responsive map of all cell columns with border right
 * @param { Cell<TableBody, unknown> } props.cell - The column definition cell
 * @returns {ReactElement} The rendered TableCell component.
 */
export const TableCell = memo(
  <TableBody, ColumnTableNames>({
    cell,
    cellsWithBorderRightMap,
    ...rest
  }: TableCellProps<TableBody, ColumnTableNames>) => {
    /**
     * Determines the border right style for the cell based on the header and width mapping.
     */
    const borderRight = useBorderRight<ColumnTableNames>({
      cellId: cell.id,
      cellsWithBorderRightMap,
    });

    const size = `${cell.column.getSize()}px`;

    return (
      <Td w={size} maxW={size} p={0} borderRight={borderRight} borderBottom='none' {...rest}>
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </Td>
    );
  }
);
